import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plus, Search, Download, Calendar, AlertCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Grant {
  id: string;
  name: string;
  organization: string;
  amount: number;
  status: 'active' | 'pending' | 'completed' | 'rejected';
  startDate: string;
  endDate: string;
  progress: number;
  nextDeliverable: string;
}

const mockGrants: Grant[] = [
  {
    id: "1",
    name: "Wildlife Conservation Initiative",
    organization: "Global Nature Fund",
    amount: 250000,
    status: 'active',
    startDate: "2024-01-01",
    endDate: "2024-12-31",
    progress: 75,
    nextDeliverable: "Q2 Progress Report"
  },
  {
    id: "2",
    name: "Habitat Restoration Project",
    organization: "Environmental Protection Agency",
    amount: 180000,
    status: 'pending',
    startDate: "2024-04-01",
    endDate: "2025-03-31",
    progress: 0,
    nextDeliverable: "Initial Assessment"
  },
  // Add more grants...
];

export function GrantsManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const getStatusColor = (status: Grant['status']) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'completed': return 'bg-blue-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Grants & Donations</h2>
          <p className="text-gray-500">Manage your grants and track donations</p>
        </div>
        <div className="flex gap-4">
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Grant
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search grants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grants List */}
      <div className="space-y-4">
        {mockGrants.map((grant) => (
          <Card key={grant.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-lg">{grant.name}</h3>
                    <Badge variant="secondary" className={getStatusColor(grant.status)}>
                      {grant.status}
                    </Badge>
                  </div>
                  <p className="text-gray-500">{grant.organization}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(grant.startDate).toLocaleDateString()} - {new Date(grant.endDate).toLocaleDateString()}
                    </span>
                    <span className="font-medium text-gray-900">
                      ${grant.amount.toLocaleString()}
                    </span>
                  </div>
                </div>
                <Button variant="outline">View Details</Button>
              </div>
              <div className="mt-4">
                <div className="flex justify-between items-center text-sm mb-2">
                  <span>Progress</span>
                  <span>{grant.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${grant.progress}%` }}
                  />
                </div>
              </div>
              {grant.nextDeliverable && (
                <div className="mt-4 flex items-center gap-2 text-sm text-yellow-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>Next Deliverable: {grant.nextDeliverable}</span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}